import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, Loader2, Building2, Check, ArrowRight, ArrowLeft, Trash2, Pencil } from "lucide-react";
import AvidaLogo from "@/components/brand/AvidaLogo";
import { useWizard } from "@/context/WizardContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SELECTED_KEY = "avida-selected-org-v1";

/** Apply the selected org's fields to `data.agency` on the wizard. */
function applyOrgToWizard(setData, org) {
  setData((d) => ({
    ...d,
    agency: {
      ...(d.agency || {}),
      name: org.name || "",
      provider_number: org.provider_number || "",
      npi: org.npi || "",
      phone: org.phone || "",
      fax: org.fax || "",
      address: [org.address_line1, org.city, org.state, org.zip].filter(Boolean).join(", "),
    },
  }));
}

/** Modal: list organizations, let the nurse pick one, create/edit/delete up to 5.
 *  On pick, writes to wizard agency + localStorage + navigates to /wizard/1.
 */
export default function OrganizationPicker({ open, onClose, onAfterSelect }) {
  const navigate = useNavigate();
  const { setData } = useWizard();
  const [loading, setLoading] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [max, setMax] = useState(5);
  const [editing, setEditing] = useState(null); // null | "new" | org_id
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/organizations`)
      .then((r) => { setOrgs(r.data.organizations || []); setMax(r.data.max || 5); })
      .catch((e) => toast.error(`Failed to load organizations: ${e.message}`))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const onSelect = (org) => {
    try { localStorage.setItem(SELECTED_KEY, JSON.stringify({ id: org.id, name: org.name, selected_at: Date.now() })); } catch { /* non-fatal */ }
    applyOrgToWizard(setData, org);
    toast.success(`Loaded ${org.name} — agency fields pre-populated.`);
    onAfterSelect?.(org);
    onClose?.();
    navigate("/wizard/1");
  };

  const onSave = async (form) => {
    setBusy(true);
    try {
      if (editing === "new") {
        const r = await axios.post(`${API}/organizations`, form);
        setOrgs((prev) => [...prev, r.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`${r.data.name} added.`);
      } else {
        const r = await axios.put(`${API}/organizations/${editing}`, form);
        setOrgs((prev) => prev.map((o) => (o.id === editing ? r.data : o)));
        toast.success(`${r.data.name} updated.`);
      }
      setEditing(null);
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (org) => {
    if (!window.confirm(`Delete "${org.name}"?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/organizations/${org.id}`);
      setOrgs((prev) => prev.filter((o) => o.id !== org.id));
      toast.success(`${org.name} removed.`);
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[150] bg-[#0B1E3A]/55 backdrop-blur-sm flex items-center justify-center px-4 py-8"
      data-testid="org-picker-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[86vh] flex flex-col shadow-2xl border border-[#D6E3EF]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-7 py-5 border-b border-[#E7EEF3] flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-[#FFDA0B] p-2">
              <Building2 className="w-5 h-5 text-[#072A54]" strokeWidth={2} />
            </div>
            <div>
              <div className="eyebrow mb-1">Organization</div>
              <h3 className="font-display text-[20px] font-semibold text-[#0B1E3A] tracking-tight">
                Which agency is this assessment for?
              </h3>
              <div className="text-[12.5px] text-[#3A5275] mt-0.5">
                Up to {max} organizations. Selecting one pre-populates the agency info on Step 1.
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-[#3A5275] hover:text-[#0B1E3A] p-1" data-testid="org-picker-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {editing !== null ? (
            <OrgForm
              initial={editing === "new" ? {} : orgs.find((o) => o.id === editing)}
              onCancel={() => setEditing(null)}
              onSave={onSave}
              busy={busy}
            />
          ) : loading ? (
            <div className="flex items-center gap-2 text-[#3A5275] p-6" data-testid="org-picker-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading organizations…
            </div>
          ) : (
            <div className="space-y-2" data-testid="org-picker-list">
              {orgs.length === 0 && (
                <div className="text-center py-10">
                  <div className="font-display text-lg text-[#0B1E3A] mb-1">No organizations yet</div>
                  <div className="text-[13px] text-[#3A5275] mb-4">Add your first agency to begin.</div>
                </div>
              )}
              {orgs.map((o) => (
                <div
                  key={o.id}
                  className="rounded-xl border border-[#D6E3EF] hover:border-[#0A3D79] px-4 py-3 flex items-center gap-3 transition bg-white"
                  data-testid={`org-row-${o.id}`}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(o)}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`org-select-${o.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#0B1E3A] text-[15px]">{o.name}</span>
                      {o.is_default && (
                        <span className="avida-accent-chip !text-[9.5px] !px-2 !py-[2px]">Default</span>
                      )}
                    </div>
                    <div className="text-[12.5px] text-[#3A5275] mt-0.5 flex flex-wrap gap-x-3">
                      {o.address_line1 && <span>{o.address_line1}, {o.city}</span>}
                      {o.phone && <span>{o.phone}</span>}
                      {o.provider_number && <span>MA {o.provider_number}</span>}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(o.id)}
                    className="p-2 rounded-md text-[#3A5275] hover:bg-[#F0F6FC] hover:text-[#0A3D79]"
                    aria-label="Edit"
                    data-testid={`org-edit-${o.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(o)}
                    className="p-2 rounded-md text-[#c05746] hover:bg-[#fbeae5]"
                    aria-label="Delete"
                    data-testid={`org-delete-${o.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onSelect(o)}
                    className="btn-primary !h-9 !px-3 !text-[13px]"
                    data-testid={`org-use-${o.id}`}
                  >
                    Use <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {orgs.length < max && (
                <button
                  type="button"
                  onClick={() => setEditing("new")}
                  className="w-full rounded-xl border-2 border-dashed border-[#D6E3EF] hover:border-[#DBB90A] hover:bg-[#FFFDEC] text-[#3A5275] hover:text-[#0A3D79] px-4 py-4 transition flex items-center justify-center gap-2 font-semibold"
                  data-testid="org-add-new"
                >
                  <Plus className="w-4 h-4" /> Add organization ({orgs.length}/{max})
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E7EEF3] bg-[#F7FAFC] flex items-center justify-between">
          <span className="text-[11.5px] text-[#3A5275] inline-flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5 text-[#0A3D79]" />
            Selection pre-fills Step 1 and is remembered on this device.
          </span>
          <Link to="/" onClick={onClose} className="text-[12.5px] text-[#3A5275] hover:text-[#0A3D79]">
            Cancel
          </Link>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// Inner form for add/edit
// ===================================================================
function OrgForm({ initial = {}, onCancel, onSave, busy }) {
  const [form, setForm] = useState({
    name:            initial.name || "",
    address_line1:   initial.address_line1 || "",
    city:            initial.city || "",
    state:           initial.state || "WI",
    zip:             initial.zip || "",
    phone:           initial.phone || "",
    fax:             initial.fax || "",
    provider_number: initial.provider_number || "",
    npi:             initial.npi || "",
    is_default:      initial.is_default || false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSave = form.name.trim().length > 1;

  return (
    <div className="space-y-3" data-testid="org-form">
      <button
        type="button"
        onClick={onCancel}
        className="text-[12.5px] text-[#3A5275] hover:text-[#0A3D79] inline-flex items-center gap-1"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to list
      </button>
      <div>
        <div className="field-label">Agency name *</div>
        <input className="tb-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Avida Personal Care" data-testid="org-form-name" />
      </div>
      <div>
        <div className="field-label">Street address</div>
        <input className="tb-input" value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} placeholder="790 N. Milwaukee #355" data-testid="org-form-address" />
      </div>
      <div className="grid grid-cols-[2fr_1fr_1fr] gap-3">
        <div>
          <div className="field-label">City</div>
          <input className="tb-input" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Milwaukee" data-testid="org-form-city" />
        </div>
        <div>
          <div className="field-label">State</div>
          <input className="tb-input" value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="WI" data-testid="org-form-state" />
        </div>
        <div>
          <div className="field-label">Zip</div>
          <input className="tb-input" value={form.zip} onChange={(e) => set("zip", e.target.value)} placeholder="53202" data-testid="org-form-zip" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="field-label">Phone</div>
          <input className="tb-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="414-348-4600" data-testid="org-form-phone" />
        </div>
        <div>
          <div className="field-label">Fax</div>
          <input className="tb-input" value={form.fax} onChange={(e) => set("fax", e.target.value)} placeholder="262-672-4496" data-testid="org-form-fax" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="field-label">Medicaid provider number</div>
          <input className="tb-input" value={form.provider_number} onChange={(e) => set("provider_number", e.target.value)} placeholder="100408742" data-testid="org-form-provider" />
        </div>
        <div>
          <div className="field-label">NPI</div>
          <input className="tb-input" value={form.npi} onChange={(e) => set("npi", e.target.value)} placeholder="1386537025" data-testid="org-form-npi" />
        </div>
      </div>
      <label className="inline-flex items-center gap-2 text-[13px] text-[#3A5275] pt-2">
        <input
          type="checkbox"
          checked={form.is_default}
          onChange={(e) => set("is_default", e.target.checked)}
          data-testid="org-form-default"
        />
        Set as default for new sessions
      </label>
      <div className="flex justify-end gap-2 pt-3 border-t border-[#E7EEF3] mt-4">
        <button type="button" onClick={onCancel} className="btn-ghost" disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSave(form)}
          className="btn-primary"
          disabled={!canSave || busy}
          data-testid="org-form-save"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save
        </button>
      </div>
    </div>
  );
}

// Hook for pages that want to check if an org is selected.
export function useSelectedOrg() {
  const [sel, setSel] = useState(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SELECTED_KEY);
      if (raw) setSel(JSON.parse(raw));
    } catch { /* non-fatal */ }
  }, []);
  return sel;
}

export { SELECTED_KEY };
